import type { MedicalEvent } from "@medassist/shared";
import { DEFAULT_WATER_GOAL_ML, FLUID_UNIT_ML } from "@medassist/shared";
import { saveBatchEvents, ApiError } from "../../../lib/api.js";
import { DropletIcon, UtensilsIcon } from "../../icons.js";
import type { WidgetProps } from "../types.js";

function isToday(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return (
    d.getFullYear() === now.getFullYear() &&
    d.getMonth()    === now.getMonth()    &&
    d.getDate()     === now.getDate()
  );
}

function computeWaterTodayMl(events: MedicalEvent[]): number {
  return events
    .filter((e) => e.eventType === "hydration" && isToday(e.occurredAt))
    .reduce((sum, e) => sum + (Number((e.payload as Record<string, unknown>).volumeMl) || 0), 0);
}

export function TodayWidget({ patient, events, onNavigate, onError, onPrependEvent, onShowToast }: WidgetProps) {
  const waterGoalMl   = patient.waterGoalMl ?? DEFAULT_WATER_GOAL_ML;
  const waterTodayMl  = computeWaterTodayMl(events);
  const waterPct      = Math.min(100, Math.round((waterTodayMl / waterGoalMl) * 100));
  const calorieGoal   = patient.dailyCalorieGoal;
  const caloriesToday = events
    .filter((e) => e.eventType === "food" && isToday(e.occurredAt))
    .reduce((sum, e) => sum + (Number((e.payload as Record<string, unknown>).caloriesEstimate) || 0), 0);

  async function addWater(oz: number) {
    const volumeMl = Math.round(oz * FLUID_UNIT_ML.oz);
    const payload  = { consumedAt: new Date().toISOString(), volume: oz, unit: "oz", fluidType: "water" };
    try {
      const [ref] = await saveBatchEvents(patient.patientId, [{ eventType: "hydration", payload }]);
      onPrependEvent(ref, { ...payload, volumeMl });
      onShowToast(ref, `Logged ${oz} oz water`);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Couldn't log water");
    }
  }

  return (
    <section className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <div className="mb-2 flex items-center gap-2">
        <DropletIcon size={16} className="text-(--accent)" />
        <h2 className="font-semibold text-gray-900 dark:text-gray-100">Today</h2>
      </div>
      {/* Water progress */}
      <div className="mb-1 flex items-center justify-between text-sm">
        <span className="text-gray-600 dark:text-gray-300">Water</span>
        <span className="text-gray-500 dark:text-gray-400">
          {Math.round(waterTodayMl / FLUID_UNIT_ML.oz)} / {Math.round(waterGoalMl / FLUID_UNIT_ML.oz)} oz
        </span>
      </div>
      <div className="h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-gray-800">
        <div className="h-full rounded-full bg-(--accent)" style={{ width: `${waterPct}%` }} />
      </div>
      <div className="mt-2 flex gap-2">
        <button onClick={() => void addWater(8)}  className="rounded-full border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">+8 oz</button>
        <button onClick={() => void addWater(12)} className="rounded-full border border-gray-300 dark:border-gray-700 px-3 py-1 text-xs text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800">+12 oz</button>
        <button onClick={() => onNavigate("water")} className="ml-auto text-xs text-(--accent) hover:underline">More</button>
      </div>
      {/* Nutrition */}
      <div className="mt-3 flex items-center justify-between border-t border-gray-100 dark:border-gray-800 pt-2 text-sm">
        <span className="flex items-center gap-1.5 text-gray-600 dark:text-gray-300">
          <UtensilsIcon size={14} className="text-(--accent)" /> Calories
        </span>
        <span className="text-gray-500 dark:text-gray-400">
          {Math.round(caloriesToday)}{calorieGoal ? ` / ${calorieGoal}` : ""} kcal
        </span>
      </div>
      <button onClick={() => onNavigate("food")} className="mt-1 text-xs text-(--accent) hover:underline">Log food</button>
    </section>
  );
}
